import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useSettings } from "../contexts/SettingsContext";
import { useAuth } from "../App";
import { toast } from "sonner";
import { DisplayTab } from "../components/settings/DisplayTab";
import { ColorsTab } from "../components/settings/ColorsTab";
import { DashboardTab } from "../components/settings/DashboardTab";
import { SelfAssessmentSettingsTab } from "../components/settings/SelfAssessmentSettingsTab";
import { ReportsTab } from "../components/settings/ReportsTab";
import { NotificationsTab } from "../components/settings/NotificationsTab";
import { DataManagementTab } from "../components/settings/DataManagementTab";
import { AdvisoryGroupsTab } from "../components/settings/AdvisoryGroupsTab";
import { ClassGroupsTab } from "../components/settings/ClassGroupsTab";
import { BlockScheduleTab } from "../components/settings/BlockScheduleTab";
import { SemestersTab } from "../components/settings/SemestersTab";
import { AuditLogTab } from "../components/settings/AuditLogTab";
import ProficiencyCatalogManager from "../components/settings/ProficiencyCatalogManager";
import SchoolBrandingTab from "../components/settings/SchoolBrandingTab";
import HhbTemplatesTab from "../components/settings/HhbTemplatesTab";
import BullyingDetectionTab from "../components/settings/BullyingDetectionTab";
import { CommunicationTypesTab } from "../components/settings/CommunicationTypesTab";
import { IncidentTypesTab } from "../components/settings/IncidentTypesTab";
import { RiskConfigTab } from "../components/settings/RiskConfigTab";
import { NotificationsEmailTab } from "../components/settings/NotificationsEmailTab";
import { BillingRatesTab } from "../components/settings/BillingRatesTab";
import { SetupWizardContent } from "../components/SetupWizard";

const ADMIN_TABS = [
  "class-groups", "advisory", "block-schedule", "semesters", "self-assess",
  "data", "proficiency", "branding", "hhb-templates", "bullying-detection", "communication-types", "risk",
  "notifications", "setup-wizard", "audit", "billing-rates", "incident-types",
];

export default function SettingsPage() {
  const { settings, updateSetting, updateSettings, resetSettings, defaultSettings, synced, syncing } = useSettings();
  const { isAdmin } = useAuth();
  const [tempPrimary, setTempPrimary] = useState(settings.primaryColor);
  const [tempAccent, setTempAccent] = useState(settings.accentColor);
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultTab = isAdmin() ? "class-groups" : "personalize";
  const allowedTabs = isAdmin() ? [...ADMIN_TABS, "personalize"] : ["personalize"];
  const urlTab = searchParams.get("tab");
  const activeTab = allowedTabs.includes(urlTab) ? urlTab : defaultTab;
  const setActiveTab = (v) => {
    if (v === defaultTab) setSearchParams({}, { replace: true });
    else setSearchParams({ tab: v }, { replace: true });
  };

  const handleReset = () => {
    if (window.confirm("Reset all settings to defaults?")) {
      resetSettings();
      setTempPrimary(defaultSettings.primaryColor);
      setTempAccent(defaultSettings.accentColor);
      toast.success("Settings reset to defaults");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--primary-color)' }}>Settings</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-muted-foreground">Customize your SMARTIS experience</p>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
              syncing ? 'bg-amber-100 text-amber-700' :
              synced ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {syncing ? (
                <>
                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Syncing...
                </>
              ) : synced ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  Synced to account
                </>
              ) : 'Local only'}
            </span>
          </div>
        </div>
        <Button variant="outline" onClick={handleReset} data-testid="reset-settings">Reset to Defaults</Button>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="settings-legend">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-600 inline-block" /> Academic structure</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-600 inline-block" /> Assessments &amp; tracking</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-600 inline-block" /> Communication</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-700 inline-block" /> System &amp; data</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500 inline-block" /> Personal</span>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          {/* Academic structure (blue) */}
          {isAdmin() && <TabsTrigger value="class-groups" data-testid="tab-class-groups" className="bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Classes/Groups</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="advisory" data-testid="tab-advisory" className="bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Advisory Groups</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="block-schedule" data-testid="tab-block-schedule" className="bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Block Schedule</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="semesters" data-testid="tab-semesters" className="bg-blue-50 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Semesters</TabsTrigger>}

          {/* Assessments & tracking (emerald) */}
          {isAdmin() && <TabsTrigger value="self-assess" data-testid="tab-self-assess" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Self-Assessment</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="risk" data-testid="tab-risk-config" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Risk Assessment</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="bullying-detection" data-testid="tab-bullying-detection" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Bullying &amp; Harassment Detection</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="proficiency" data-testid="tab-proficiency-catalog" className="bg-emerald-50 text-emerald-700 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Proficiency Indicators</TabsTrigger>}

          {/* Communication (violet) */}
          {isAdmin() && <TabsTrigger value="communication-types" data-testid="tab-communication-types" className="bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white">Communication Types</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="incident-types" data-testid="tab-incident-types" className="bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white">Incident Types</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="notifications" data-testid="tab-notifications-email" className="bg-violet-50 text-violet-700 data-[state=active]:bg-violet-600 data-[state=active]:text-white">Notifications &amp; Email</TabsTrigger>}

          {/* System & data (slate) */}
          {isAdmin() && <TabsTrigger value="data" data-testid="tab-data" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">Data Management</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="billing-rates" data-testid="tab-billing-rates" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">Billing Rates</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="branding" data-testid="tab-branding" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">School Branding</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="hhb-templates" data-testid="tab-hhb-templates" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">HHB Letters</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="setup-wizard" data-testid="tab-setup-wizard" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">Set-Up Wizard</TabsTrigger>}
          {isAdmin() && <TabsTrigger value="audit" data-testid="tab-audit" className="bg-slate-100 text-slate-700 data-[state=active]:bg-slate-700 data-[state=active]:text-white">Audit Log</TabsTrigger>}

          {/* Personal (amber) */}
          <TabsTrigger value="personalize" data-testid="tab-personalize" className="bg-amber-50 text-amber-700 data-[state=active]:bg-amber-600 data-[state=active]:text-white">Personalize</TabsTrigger>
        </TabsList>

        {isAdmin() && <TabsContent value="class-groups"><ClassGroupsTab /></TabsContent>}
        {isAdmin() && <TabsContent value="advisory"><AdvisoryGroupsTab /></TabsContent>}
        {isAdmin() && <TabsContent value="block-schedule"><BlockScheduleTab /></TabsContent>}
        {isAdmin() && <TabsContent value="semesters"><SemestersTab /></TabsContent>}
        {isAdmin() && <TabsContent value="self-assess"><SelfAssessmentSettingsTab /></TabsContent>}
        {isAdmin() && <TabsContent value="risk"><RiskConfigTab /></TabsContent>}
        {isAdmin() && <TabsContent value="bullying-detection"><BullyingDetectionTab /></TabsContent>}
        {isAdmin() && <TabsContent value="communication-types"><CommunicationTypesTab /></TabsContent>}
        {isAdmin() && <TabsContent value="incident-types"><IncidentTypesTab /></TabsContent>}
        {isAdmin() && <TabsContent value="notifications"><NotificationsEmailTab /></TabsContent>}
        {isAdmin() && <TabsContent value="data"><DataManagementTab /></TabsContent>}
        {isAdmin() && <TabsContent value="billing-rates"><BillingRatesTab /></TabsContent>}
        {isAdmin() && <TabsContent value="proficiency"><ProficiencyCatalogManager /></TabsContent>}
        {isAdmin() && <TabsContent value="branding"><SchoolBrandingTab /></TabsContent>}
        {isAdmin() && <TabsContent value="hhb-templates"><HhbTemplatesTab /></TabsContent>}
        {isAdmin() && <TabsContent value="setup-wizard"><SetupWizardContent /></TabsContent>}
        {isAdmin() && <TabsContent value="audit"><AuditLogTab /></TabsContent>}
        <TabsContent value="personalize">
          <div className="space-y-6">
            <DisplayTab settings={settings} updateSetting={updateSetting} />
            <ColorsTab settings={settings} updateSetting={updateSetting} updateSettings={updateSettings}
              tempPrimary={tempPrimary} setTempPrimary={setTempPrimary} tempAccent={tempAccent} setTempAccent={setTempAccent} />
            <DashboardTab settings={settings} updateSetting={updateSetting} />
            <ReportsTab settings={settings} updateSetting={updateSetting} />
            <NotificationsTab settings={settings} updateSetting={updateSetting} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
